import { FC } from 'react';
import { ProductoData } from '../../model/ProductoData';
import { VStack } from '@bpba/ui-components';
import { Spinner } from '../shared/Spinner';
import { getAddressString } from './DireccionResumen';
import { FormularioDireccionNueva } from './FormularioDireccionNueva';
import { FormularioOtp } from './FormularioOtp';
import { useCanje } from './useCanje';
import { CANJE_DEFAULT_ERROR_MESSAGE, CANJE_PRODUCTO_CONFIGS } from '../../constants/data';
import { FormularioTelefono } from './FormularioTelefono';
import { StatusContainer } from './StatusContainer';
import { CanjeStatus } from './useCanjeStatus';
import { phoneFormat } from './TelefonoResumen';
import { CanjeBreadcrumb } from './CanjeBreadcrumb';
import { Modal } from '../shared/modal/Modal';
import { MensajeExitoso } from './MensajeExitoso';
import { InputSelectContainer } from './InputSelectContainer';
import { FormularioSmiles } from './FormularioSmiles';
import { ResumenContainer } from './ResumenContainer';
import { OpenCanjeFn } from './useCanjeActivation';

const ADDRESS_FORM_KEY = 'address';
const PHONE_FORM_KEY = 'phone';

type Props = {
  show: boolean;
  product?: ProductoData;
  close?: () => void;
  reopen?: OpenCanjeFn;
  updateStock?: () => void;
};

export const CanjeDialog: FC<Props> = ({ show, close, reopen, product, updateStock }) => {
  const [
    status,
    isLoading,
    isError,
    isSuccessful,
    showOTP,
    requestErrorMessage,
    addressFormData,
    onClose,
    otp,
    setOtp,
    phoneFormData,
    smilesData,
    existingAddresses,
    existingPhones,
    confirm,
    firstElementRef,
  ] = useCanje(show, ADDRESS_FORM_KEY, PHONE_FORM_KEY, close, product, reopen, updateStock);

  const showForm = !isLoading && !isSuccessful;
  const errorMessage =
    requestErrorMessage && typeof requestErrorMessage === 'string' && requestErrorMessage.trim().length > 0
      ? requestErrorMessage
      : CANJE_DEFAULT_ERROR_MESSAGE;
  const isFormPart1 = status === CanjeStatus.FORMULARIO;
  const isPhysicalProduct = product?.esFisico();

  function getConfirmLabel() {
    if (isSuccessful || isError) return 'Finalizar';

    if (isFormPart1) return undefined;

    return '';
  }

  function getConfirmAction() {
    if (isFormPart1) return confirm;

    return onClose;
  }

  if (!product) return <></>;

  const productConfig = CANJE_PRODUCTO_CONFIGS.get(product.tipoProducto);

  return (
    <Modal
      show={show}
      title={productConfig?.tituloModal}
      showConfirmButton={isFormPart1 || isSuccessful || status === CanjeStatus.ERROR}
      confirmLabel={getConfirmLabel()}
      onConfirm={getConfirmAction()}
      onCancel={onClose}
      showCancelButton={isFormPart1}
      showErrorMessage={isError}
      errorMessage={errorMessage}
    >
      <VStack>
        <CanjeBreadcrumb status={status} />

        {isLoading && <Spinner />}
        <MensajeExitoso show={isSuccessful} productConfig={productConfig} />

        <VStack display={showForm ? 'flex' : 'none'} maxWidth={[450]} width={'100%'} marginTop={['0px']}>
          <StatusContainer optionStatus={CanjeStatus.FORMULARIO} currentStatus={status} show={isPhysicalProduct}>
            <InputSelectContainer
              title="Seleccioná una dirección de envío"
              titleNewButton="Dirección nueva"
              placeholderTitle="Tus direcciones"
              dataOptionsList={existingAddresses}
              formData={addressFormData}
              getLabel={getAddressString}
              inputDataChildren={FormularioDireccionNueva}
              name={ADDRESS_FORM_KEY}
              customRef={firstElementRef}
            />
          </StatusContainer>

          <StatusContainer optionStatus={CanjeStatus.FORMULARIO} currentStatus={status} show={product.esProgramaSmiles()}>
            <FormularioSmiles smilesData={smilesData} customRef={firstElementRef} />
          </StatusContainer>

          <StatusContainer optionStatus={CanjeStatus.FORMULARIO} currentStatus={status}>
            <InputSelectContainer
              title="Seleccioná un teléfono de contacto"
              titleNewButton="Teléfono nuevo"
              placeholderTitle="Tus teléfonos"
              dataOptionsList={existingPhones}
              formData={phoneFormData}
              getLabel={phoneFormat}
              inputDataChildren={FormularioTelefono}
              name={PHONE_FORM_KEY}
              customRef={firstElementRef}
            />
          </StatusContainer>

          <StatusContainer optionStatus={[CanjeStatus.OTP, CanjeStatus.ERROR_OTP_STATUS]} currentStatus={status}>
            <ResumenContainer
              product={product}
              productConfig={productConfig}
              getPhone={phoneFormData.getValue}
              getAddress={addressFormData.getValue}
              getSmilesData={smilesData.getValue}
            />
            <FormularioOtp otp={otp} setOtp={setOtp} onComplete={confirm} ariaHidden={!showOTP} />
          </StatusContainer>
        </VStack>
      </VStack>
    </Modal>
  );
};
